import { useBemCN } from "@gik/core/utils/bemBlock";
import React from "react";
import type { Color, ColorResult, InjectedColorProps } from "react-color";
import type { BaseColorPickerProps } from "./components/BaseColorPicker";
import { BaseColorPicker } from "./components/BaseColorPicker";
import { SwatchHexPill } from "./components/SwatchHexPill";

export type ColorPickerProps = {
	value?: Color;
	defaultColor?: Color;
	onChange?: (color: Color) => void;
} & BaseColorPickerProps;

// Default header for the solid picker: full-width preview bar + swatch/hex row.
function SolidHeader({ hex, onChange }: InjectedColorProps) {
	const bem = useBemCN("solid-color-picker");
	return (
		<div {...bem("header")}>
			<div {...bem("preview")} style={{ backgroundColor: hex }} />
			<SwatchHexPill
				color={hex}
				onChange={onChange as (hex: string) => void}
			/>
		</div>
	);
}

export function ColorPicker({
	value,
	defaultColor = "#000000",
	onChange,
	// Pass `renderHeader={null}` to embed a header-less picker (e.g. inside the
	// gradient picker which renders its own header).
	renderHeader = (props) => <SolidHeader {...props} />,
	...otherProps
}: ColorPickerProps) {
	// ponytail: no local color state — react-color's CustomPicker (wrapping
	// BaseColorPicker) already syncs its internal HSL from the `color` prop, so
	// we drive it straight from `value`. This kills the old blank-div remount
	// flash that fired every time the selected gradient stop changed.
	const _color = value || defaultColor;

	const handleChange = React.useCallback(
		(colorResult: ColorResult) => {
			onChange?.(colorResult?.hex);
		},
		[onChange],
	);

	return (
		<BaseColorPicker
			{...otherProps}
			color={_color}
			onChange={handleChange}
			renderHeader={renderHeader}
		/>
	);
}
